import { memo } from 'react';
import { Info, Plus } from 'lucide-react';
import { Button, Skeleton, StatusMessage } from '@aimsos/ui';
import useToolsPage from '../../hooks/tools/useToolsPage.js';
import SelectedTools from './SelectedTools.jsx';
import SlideoutTools from './SlideoutTools.jsx';
import ToolConfigurationSlideout from './ToolConfigurationSlideout.jsx';

/**
 * @param {{
 *   t: (key: string, opts?: object) => string,
 *   agentId: string | undefined,
 *   agentReady: boolean,
 *   disabled: boolean,
 * }} props
 */
const ToolsTabPanel = memo(({ t, agentId, agentReady, disabled }) => {
  const agentLoading = !agentReady;

  const {
    installedTools,
    availableTools,
    loading,
    loadingToolsList,
    showSlideoutTools,
    showConfigurationSlideout,
    selectedToolForConfiguration,
    libraryDrawerConfigTool,
    handleToggleTool,
    handleDeleteTool,
    handleAddTools,
    handleOpenSlideoutTools,
    handleCloseSlideoutTools,
    handleOpenConfiguration,
    handleCloseConfiguration,
    handleGoBackFromLibraryConfigure,
    handleSaveLibraryDrawerConfiguration,
    handleSaveConfiguration,
  } = useToolsPage({ agentId, agentLoading, t });

  if (disabled) {
    return (
      <StatusMessage
        icon={Info}
        title={t('edit.tools.needAgentTitle')}
        subtitle={t('edit.tools.needAgentSubtitle')}
      />
    );
  }

  if (loading && installedTools.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton height={32} width={256} borderRadius={8} />
        <Skeleton height={96} width="100%" borderRadius={8} />
        <Skeleton height={96} width="100%" borderRadius={8} />
      </div>
    );
  }

  return (
    <>
      <div className="flex w-full flex-col pb-6">
        <div className="mb-6 flex min-h-[60px] items-start justify-between gap-4">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <h1 className="text-base font-semibold text-content">{t('edit.tools.pageTitle')}</h1>
            <p className="text-sm text-content-muted">{t('edit.tools.pageSubtitle')}</p>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="md"
            leftIcon={Plus}
            onClick={handleOpenSlideoutTools}
          >
            {t('edit.tools.add')}
          </Button>
        </div>

        <SelectedTools
          t={t}
          installedTools={installedTools}
          onToggleTool={handleToggleTool}
          onSettings={handleOpenConfiguration}
          onDelete={handleDeleteTool}
        />
      </div>

      <SlideoutTools
        t={t}
        open={showSlideoutTools}
        onClose={handleCloseSlideoutTools}
        onAddTools={handleAddTools}
        onToolConfiguration={handleOpenConfiguration}
        installedTools={installedTools}
        availableTools={availableTools}
        isLoading={loadingToolsList}
        libraryConfigureTool={libraryDrawerConfigTool}
        onLibraryConfigureBack={handleGoBackFromLibraryConfigure}
        onLibraryConfigureSave={handleSaveLibraryDrawerConfiguration}
      />

      <ToolConfigurationSlideout
        t={t}
        open={showConfigurationSlideout}
        onClose={handleCloseConfiguration}
        tool={selectedToolForConfiguration}
        onSave={handleSaveConfiguration}
      />
    </>
  );
});

ToolsTabPanel.displayName = 'ToolsTabPanel';

export default ToolsTabPanel;
